// SAB-575: /signup — marketing-first account creation. Collects EMAIL + PASSWORD
// ONLY (KYC rule F: verification fires at deposit, never here — league setup must
// not read the verification flag). Social-first. Commissioner intent rides in the
// route (?intent=create-league) so it survives the login hop and the OAuth
// round-trip. Already-authed users skip this screen entirely.
import { authErrorCopy } from '@/lib/authErrorCopy';
import { useState } from 'react';
import { Navigate, Link, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { Eye, EyeOff, ShieldCheck, Briefcase, Loader2, Mail, ArrowRight } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { AuthEntryLayout } from '@/components/auth/AuthEntryLayout';
import { Capacitor } from '@capacitor/core';
import { useNativeAppVersion } from '@/hooks/useNativeAppVersion';
import { SocialAuthButtons } from '@/components/auth/SocialAuthButtons';
import { NATIVE_OAUTH_MIN_VERSION } from '@/lib/nativeOAuthGate';
import { PasswordStrengthMeter } from '@/components/auth/PasswordStrengthMeter';
import { useOAuthSignIn } from '@/hooks/useOAuthSignIn';
import {
  resolveAuthTarget, hasCreateLeagueIntent, SIGNUP_VERIFICATION_NOTE, authHref,
} from '@/lib/landingAuthCopy';

const FIELD =
  'h-[52px] w-full rounded-xl px-4 text-[15px] text-foreground placeholder:text-muted-foreground/70 outline-none focus:ring-2 focus:ring-ring';
const FIELD_STYLE = { background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.14)' } as const;
const LABEL = 'block text-[12.5px] font-semibold text-white/55 mb-1.5';

export default function SignUp() {
  const { user, isLoading } = useAuth();
  const { atLeast } = useNativeAppVersion();
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  const { toast } = useToast();

  const intent = params.get('intent');
  const isCreateLeague = hasCreateLeagueIntent(intent);
  // SAB-592: the primary invitee path. An invite link routes new users to
  // /signup?returnTo=/nfl/join/CODE. A valid returnTo becomes the target, and
  // because emailRedirectTo below is `${origin}${target}`, it survives the email
  // confirmation round trip too (AuthConfirm forwards to it after verifyOtp).
  const returnTo = params.get('returnTo');
  const from = (location.state as { from?: string })?.from ?? null;
  const target = resolveAuthTarget(intent, from, returnTo);
  const loginHref = authHref('/login', intent, returnTo);

  const { signInWith, pending: oauthPending } = useOAuthSignIn(target);

  const prefillEmail = (location.state as { email?: string })?.email ?? '';
  const [email, setEmail] = useState(prefillEmail);
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  // Case 1: never show an auth screen to an authed user — skip straight to target.
  if (!isLoading && user) return <Navigate to={target} replace />;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          // Intent survives email confirmation: the confirm link lands at the target.
          emailRedirectTo: `${window.location.origin}${target}`,
          data: { full_name: email.split('@')[0] },
        },
      });
      if (error) {
        const c = authErrorCopy(error, 'signup');
        console.error('[auth] sign-up failed:', c.detail);
        toast({ title: c.title, description: c.description, variant: 'destructive' });
        return;
      }
      // If email confirmation is OFF, a session exists now → go straight to target.
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) { navigate(target, { replace: true }); return; }
      // Otherwise confirmation is required — show the check-your-email interstitial.
      setSent(true);
    } finally {
      setSubmitting(false);
    }
  };

  if (sent) {
    return (
      <AuthEntryLayout
        headline="Check your email"
        subhead={`We sent a confirmation link to ${email}. Open it to finish${isCreateLeague ? ' and jump into league setup' : ''}.`}
        headerPrompt="Wrong address?"
        headerLinkLabel="Sign Up"
        headerLinkTo="/signup"
      >
        <div className="flex items-center gap-3 rounded-xl p-4" style={FIELD_STYLE}>
          <Mail className="h-5 w-5 text-[hsl(var(--primary))] shrink-0" />
          <p className="text-sm text-muted-foreground">Didn't get it? Check spam, or try signing up again.</p>
        </div>
      </AuthEntryLayout>
    );
  }

  const chip = isCreateLeague ? (
    <span
      className="inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-[12px] font-bold uppercase tracking-[0.04em] text-[hsl(var(--primary))]"
      style={{ background: 'hsl(25 95% 53% / 0.12)', border: '1px solid hsl(25 95% 53% / 0.3)' }}
    >
      <Briefcase className="h-3.5 w-3.5" />
      Step 1 of 2 · Creating a league
    </span>
  ) : null;

  return (
    <AuthEntryLayout
      headline="Create your account"
      subhead={isCreateLeague ? "Free. Next you'll name your league and invite your players." : undefined}
      chip={chip}
      headerPrompt="Have an account?"
      headerLinkLabel="Log In"
      headerLinkTo={loginHref}
    >
      <SocialAuthButtons onProvider={signInWith} disabled={oauthPending || submitting} />

      {/* SAB (native OAuth gate): gate the "or" divider too so it isn't orphaned when
          SocialAuthButtons returns null on native. On native the form follows the
          header directly. */}
      {(!Capacitor.isNativePlatform() || atLeast(NATIVE_OAUTH_MIN_VERSION)) && (
        <div className="flex items-center gap-3 my-6">
          <div className="flex-1 h-px" style={{ background: 'rgba(255,255,255,0.1)' }} />
          <span className="text-xs text-muted-foreground">or</span>
          <div className="flex-1 h-px" style={{ background: 'rgba(255,255,255,0.1)' }} />
        </div>
      )}

      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <div>
          <label htmlFor="su-email" className={LABEL}>Email</label>
          <input id="su-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
            placeholder="you@email.com" className={FIELD} style={FIELD_STYLE} />
        </div>
        <div>
          <label htmlFor="su-password" className={LABEL}>Password</label>
          <div className="relative">
            <input id="su-password" type={showPw ? 'text' : 'password'} required minLength={8}
              value={password} onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••••" className={`${FIELD} pr-11`} style={FIELD_STYLE} />
            <button type="button" onClick={() => setShowPw(!showPw)} tabIndex={-1}
              aria-label={showPw ? 'Hide password' : 'Show password'}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
              {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
          <PasswordStrengthMeter password={password} />
        </div>

        <button type="submit" disabled={submitting || oauthPending}
          className="mt-1 h-[54px] w-full rounded-xl font-display font-bold text-[17px] text-primary-foreground bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary)/0.9)] transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
          {submitting ? <><Loader2 className="h-5 w-5 animate-spin" />Creating…</>
            : isCreateLeague ? <>Continue to league setup <ArrowRight className="h-5 w-5" /></>
            : 'Create Account'}
        </button>
      </form>

      {/* COPY: pending legal (SAB-575) — verification note (via landingAuthCopy) */}
      <div className="mt-4 flex items-start gap-2.5 rounded-xl p-3.5" style={FIELD_STYLE}>
        <ShieldCheck className="h-4 w-4 text-[hsl(var(--primary))] shrink-0 mt-0.5" />
        <p className="text-[13px] text-muted-foreground leading-[1.5]">{SIGNUP_VERIFICATION_NOTE}</p>
      </div>

      <p className="mt-4 text-center text-[11.5px] text-muted-foreground/80 leading-[1.5]">
        By creating an account you agree to our{' '}
        <Link to="/terms" className="underline hover:text-foreground">Terms</Link> and{' '}
        <Link to="/privacy" className="underline hover:text-foreground">Privacy Policy</Link>.
      </p>
    </AuthEntryLayout>
  );
}
